// Custom horizontal bar chart matching the factsheet screenshot style
// Layout: legend section (left, flex) + bar chart section (right, fixed width)
// Includes a hidden ApexChart instance for PDF image capture
import { FC } from 'react';
import { Box } from '@mui/material';
import ReactApexChart from 'react-apexcharts';

interface IChartDataItem {
  label: string;
  value: number;
  color?: string;
}

interface CustomHorizontalBarProps {
  /** Chart data items (label + value + optional color) */
  data: IChartDataItem[];
  /** ApexChart ID used for PDF image capture via ApexCharts.exec() */
  id?: string;
  /** Bar color (defaults to #003f7a) */
  color?: string;
  /** Height of each individual bar in px (defaults to 20) */
  barHeight?: number;
  /** Fixed width of the chart (bar) section in px (defaults to 160) */
  chartWidth?: number;
}

/** Format number as German-style percentage (comma as decimal separator) */
const formatPercent = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return num.toFixed(2).replace('.', ',') + '%';
};

export const CustomHorizontalBar: FC<CustomHorizontalBarProps> = ({
  data,
  id,
  color,
  barHeight = 20,
  chartWidth = 160,
}) => {
  const maxValue = Math.max(...data.map((d) => +d.value));
  const barColor = color || '#003f7a';

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Layout: legend section (flex) + chart section (fixed width) */}
      <Box sx={{ display: 'flex' }}>
        {/* Legend section */}
        <Box sx={{ flex: 1, pr: '12px' }}>
          {data.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#D2D2D4',
                mb: '8px',
                px: '4px',
                height: barHeight,
              }}
            >
              <Box sx={{ flex: 1, fontSize: 12, color: '#000', pl: 1 }}>
                {item.label}
              </Box>
              <Box
                sx={{
                  fontSize: 12,
                  color: '#000',
                  textAlign: 'right',
                  pr: 1,
                }}
              >
                {formatPercent(item.value)}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Chart (bar) section - fixed width */}
        <Box sx={{ width: chartWidth, flexShrink: 0 }}>
          {data.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: '8px',
                height: barHeight,
              }}
            >
              <Box
                sx={{
                  width: `${(+item.value / maxValue) * 100}%`,
                  height: barHeight,
                  backgroundColor: barColor,
                  minWidth: 4,
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Hidden ApexChart for PDF image capture */}
      {id && (
        <Box
          sx={{
            overflow: 'hidden',
            height: 0,
            position: 'absolute',
            left: -9999,
          }}
        >
          <ReactApexChart
            options={{
              chart: { id },
              legend: {
                show: true,
                position: 'left',
                fontSize: '20px',
                fontFamily: 'Fira Sans',
                itemMargin: { vertical: 10 },
                markers: { size: 0 },
              },
              plotOptions: {
                bar: { horizontal: true, columnWidth: '50%' },
              },
              dataLabels: { enabled: false },
              stroke: { show: true, width: 0, colors: ['transparent'] },
              xaxis: { categories: data.map((item) => item.label) },
              yaxis: { show: true },
              fill: { opacity: 1 },
            }}
            series={[{ data: data.map((item) => item.value), color: barColor }]}
            type="bar"
            height={200}
          />
        </Box>
      )}
    </Box>
  );
};
